import { z } from 'zod';

import { BlockValueUsPhoneNumberSchema } from '../base/blockValueUsPhoneNumberSchema.js';
import { OUTBOUND_CALL_AND_SMS_ALLOWED_COUNTRIES } from '../base/phoneNumberCountries.js';

//https://docs.vapi.ai/call-forwarding#call-transfers-mode
export enum VoiceCallTransferCallMode {
  // The assistant forwards the call to the destination without any message or summary.
  BlindTransfer = 'blind-transfer',
  // The assistant forwards the call to the destination and adds a SIP header X-Transfer-Summary to the call to include the summary.
  BlindTransferAddSummaryToSipHeader = 'blind-transfer-add-summary-to-sip-header',
  // The assistant dials the destination, delivers the message to the destination party, connects the customer, and leaves the call.
  WarmTransferSayMessage = 'warm-transfer-say-message',
  //The assistant dials the destination, provides a summary of the call to the destination party, connects the customer, and leaves the call.
  WarmTransferSaySummary = 'warm-transfer-say-summary',
  //The assistant dials the destination, waits for the operator to speak, delivers the message to the destination party, and then connects the customer.
  WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage = 'warm-transfer-wait-for-operator-to-speak-first-and-then-say-message',
  //The assistant dials the destination, waits for the operator to speak, provides a summary of the call to the destination party, and then connects the customer.
  WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary = 'warm-transfer-wait-for-operator-to-speak-first-and-then-say-summary',
  // The assistant dials the destination, executes the twiml instructions on the destination call leg, connects the customer, and leaves the call
  WarmTransferTwiml = 'warm-transfer-twiml',
  // The assistant puts the customer on hold, dials the destination, and if the destination answers (and is human), delivers a message or summary before connecting the customer.
  // If the destination is unreachable or not human (e.g., with voicemail detection), the assistant delivers the fallbackMessage to the customer and optionally ends the call
  WarmTransferExperimental = 'warm-transfer-experimental',
  // The assistant dials the destination, sends an 10-digit call token via DTMF. Customer can then query notable's call api with from, to and token to get the call info.
  TransferWithDtmfCallToken = 'transfer-with-dtmf-call-token',
  // The assistant forwards the call to the destination and adds a SIP header X-Notable-Token to the call to include the token.
  BlindTransferWithTokenInSipHeader = 'blind-transfer-with-token-in-sip-header',
  // The assistant forwards the call to the destination and adds a SIP header X-Notable-VoiceCallId (UUID) to the call.
  BlindTransferWithVoiceCallIdInSipHeader = 'blind-transfer-with-voice-call-id-in-sip-header',
}

export const VoiceCallTransferCallModeToDisplayNameAndDescription: Record<
  VoiceCallTransferCallMode,
  { displayName: string; description: string }
> = {
  [VoiceCallTransferCallMode.BlindTransfer]: {
    displayName: 'Blind Transfer',
    description:
      'The assistant forwards the call to the destination without any message or summary.',
  },
  [VoiceCallTransferCallMode.BlindTransferWithTokenInSipHeader]: {
    displayName: 'Blind Transfer with Token in SIP Header (Deprecated)',
    description:
      'The assistant forwards the call to the destination and adds a SIP header X-Notable-Token to the call to include the token. Use "Blind Transfer with Voice Call ID in SIP Header" instead.',
  },
  [VoiceCallTransferCallMode.BlindTransferWithVoiceCallIdInSipHeader]: {
    displayName: 'Blind Transfer with Voice Call ID in SIP Header',
    description:
      'The assistant forwards the call to the destination and adds a SIP header X-Notable-VoiceCallId (UUID) to the call.',
  },
  [VoiceCallTransferCallMode.BlindTransferAddSummaryToSipHeader]: {
    displayName: 'Blind Transfer with Summary in SIP Header',
    description:
      'The assistant forwards the call to the destination and adds a SIP header X-Transfer-Summary to the call to include the summary.',
  },
  [VoiceCallTransferCallMode.WarmTransferSayMessage]: {
    displayName: 'Warm Transfer with Message',
    description:
      'The assistant dials the destination, delivers the message to the destination party, connects the customer, and leaves the call.',
  },
  [VoiceCallTransferCallMode.WarmTransferSaySummary]: {
    displayName: 'Warm Transfer with Summary',
    description:
      'The assistant dials the destination, provides a summary of the call to the destination party, connects the customer, and leaves the call.',
  },
  [VoiceCallTransferCallMode.WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage]: {
    displayName: 'Warm Transfer with Message after Operator speaks',
    description:
      'The assistant dials the destination, waits for the operator to speak, delivers the message to the destination party, and then connects the customer.',
  },
  [VoiceCallTransferCallMode.WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary]: {
    displayName: 'Warm Transfer with Summary after Operator speaks',
    description:
      'The assistant dials the destination, waits for the operator to speak, provides a summary of the call to the destination party, and then connects the customer.',
  },
  [VoiceCallTransferCallMode.WarmTransferTwiml]: {
    displayName: 'Warm Transfer with Twiml',
    description:
      'The assistant dials the destination, executes the twiml instructions on the destination call leg, connects the customer, and leaves the call',
  },
  [VoiceCallTransferCallMode.WarmTransferExperimental]: {
    displayName: 'Warm Transfer Experimental',
    description:
      'The assistant puts the customer on hold, dials the destination, and if the destination answers (and is human), delivers a message or summary before connecting the customer.',
  },
  [VoiceCallTransferCallMode.TransferWithDtmfCallToken]: {
    displayName: 'Transfer with DTMF Call Token',
    description:
      "The assistant dials the destination, sends an 10-digit call token via DTMF. Customer can then query notable's call api with from, to and token to get the call info.",
  },
};

export enum VoiceCallTransferCallToolDestinationType {
  Number = 'number',
  Sip = 'sip',
}

export const VoiceCallTransferDestinationTypeToVoiceCallTransferCallModes: Record<
  VoiceCallTransferCallToolDestinationType,
  VoiceCallTransferCallMode[]
> = {
  [VoiceCallTransferCallToolDestinationType.Number]: [
    VoiceCallTransferCallMode.BlindTransfer,
    VoiceCallTransferCallMode.WarmTransferSayMessage,
    VoiceCallTransferCallMode.WarmTransferSaySummary,
    VoiceCallTransferCallMode.WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage,
    VoiceCallTransferCallMode.WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary,
    VoiceCallTransferCallMode.TransferWithDtmfCallToken,
  ],
  [VoiceCallTransferCallToolDestinationType.Sip]: [
    VoiceCallTransferCallMode.BlindTransfer,
    VoiceCallTransferCallMode.BlindTransferWithTokenInSipHeader,
    VoiceCallTransferCallMode.BlindTransferWithVoiceCallIdInSipHeader,
    VoiceCallTransferCallMode.BlindTransferAddSummaryToSipHeader,
    VoiceCallTransferCallMode.WarmTransferSayMessage,
    VoiceCallTransferCallMode.WarmTransferSaySummary,
    VoiceCallTransferCallMode.WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage,
    VoiceCallTransferCallMode.WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary,
  ],
};

// https://docs.vapi.ai/api-reference/tools/create#request.body.transferCall.destinations.number.transferPlan
export const VoiceCallTransferPlanSchema = z.object({
  mode: z.enum(VoiceCallTransferCallMode),
  // This is the message the assistant will deliver to the destination party before connecting the customer.
  // Used only when mode is:
  // blind-transfer-add-summary-to-sip-header, warm-transfer-say-message,
  // warm-transfer-wait-for-operator-to-speak-first-and-then-say-message, or warm-transfer-experimental.
  message: z.string().optional(),
  // This specifies the SIP verb to use while transferring the call.
  sipVerb: z.partialRecord(z.enum(['refer', 'bye', 'dial']), z.unknown()).optional(),

  // This is the URL of the audio file to play while the call is on hold.
  // Note: omitting this for now as we won't support it
  // holdAudioUrl: z.string().optional(),

  // This is the TwiML instructions to execute on the destination call leg before connecting the customer.
  // Note: omitting this for now as we won't support it
  // twiml: z.string().optional(),

  // This is the plan for generating a summary of the call to present to the destination party.
  // Used only when mode is:
  // blind-transfer-add-summary-to-sip-header or warm-transfer-say-summary
  // or warm-transfer-wait-for-operator-to-speak-first-and-then-say-summary or warm-transfer-experimental.
  summaryPlan: z
    .object({
      // These are the messages used to generate the summary.
      // Default: [\{ "role": "system", "content": "You are an expert note-taker. You will be given a transcript of a call. Summarize the call in 2-3 sentences. DO NOT return anything except the summary." }, \{ "role": "user", "content": "Here is the transcript:\n\n\{\{transcript}}\n\n"}]
      messages: z
        .array(
          z.object({
            role: z.enum(['system', 'user']).default('system'),
            content: z.string(),
          })
        )
        .optional(),
      // This determines whether a summary is generated and stored in call.analysis.summary. Defaults to true.
      enabled: z.boolean().optional().default(true),
      // This is how long the request is tried before giving up. When request times out, call.analysis.summary will be empty.
      timeoutSeconds: z.number().optional().default(5),
    })
    .optional(),

  // This flag includes the sipHeaders from above in the refer to sip uri as url encoded query params.
  // Note: purposefully omitting the sipHeadersInReferToEnabled property for now as we are not supporting sip transfer right now
  // sipHeadersInReferToEnabled: z.boolean().optional(),

  // This configures the fallback plan when the transfer fails (destination unreachable, busy, or not human).
  fallbackPlan: z
    .object({
      // This is the message the assistant will deliver to the customer if the transfer fails.
      message: z.string(),
      // This controls what happens after delivering the failure message to the customer.
      // true: End the call after delivering the failure message (default)
      // false: Keep the assistant on the call to continue handling the customer’s request
      endCallEnabled: z.boolean().optional().default(true),
    })
    .optional(),
});

// https://docs.vapi.ai/api-reference/tools/create#request.body.transferCall.destinations.number
const VoiceCallTransferCallNumberDestinationSchema = z.object({
  type: z.literal(VoiceCallTransferCallToolDestinationType.Number),
  //This is the phone number to transfer the call to.
  number: BlockValueUsPhoneNumberSchema({
    isRequired: true,
    allowedCountries: OUTBOUND_CALL_AND_SMS_ALLOWED_COUNTRIES,
  }),
  // This is the caller ID to use when transferring the call to the number.
  callerId: z.string().optional(),
  // This is the description of the destination, used by the AI to choose when and how to transfer the call.
  description: z.string().optional(),
  // This is the extension to dial after transferring the call to the number.
  extension: z.string().optional(),
  // This is spoken to the customer before connecting them to the destination.
  message: z.string().optional(),
  // NOTE: purposefully omitting the numberE164CheckEnabled property as it always defaults to true
  // This configures how transfer is executed and the experience of the destination party receiving the call. Defaults to blind-transfer.
  transferPlan: VoiceCallTransferPlanSchema.optional(),
});

const VoiceCallTransferCallSipDestinationSchema = z.object({
  type: z.literal(VoiceCallTransferCallToolDestinationType.Sip),
  // This is the SIP URI to transfer the call to.
  sipUri: z.string(),
  // This is the description of the destination, used by the AI to choose when and how to transfer the call.
  description: z.string().optional(),
  // This is spoken to the customer before connecting them to the destination.
  message: z.string().optional(),
  // These are custom headers to be added to SIP refer during transfer call.
  sipHeaders: z.record(z.string(), z.any()).optional(),
  // This configures how transfer is executed and the experience of the destination party receiving the call. Defaults to blind-transfer.
  transferPlan: VoiceCallTransferPlanSchema.optional(),
});

export const VoiceCallTransferCallToolDestinationSchema = z.discriminatedUnion('type', [
  VoiceCallTransferCallNumberDestinationSchema,
  VoiceCallTransferCallSipDestinationSchema,
]);

export type VoiceCallTransferCallToolDestination = z.infer<
  typeof VoiceCallTransferCallToolDestinationSchema
>;
